/**
 * Shared recipient picker used by AlertRuleDrawer, RoundupEditor, and CommScheduleDrawer.
 * Supports user / team / job_tag / free email recipients with tab switching.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Stack, Chip, Autocomplete, TextField, Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as TeamIcon,
  LocalOffer as TagIcon,
  Email as EmailIcon,
} from '@mui/icons-material';

export interface Recipient {
  recipient_id?: string;
  recipient_type: 'user' | 'team' | 'job_tag' | 'email';
  user_id?: string;
  team_id?: string;
  job_tag_id?: string;
  email?: string;
  _label?: string;
}

export interface RecipientUser {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export interface RecipientTeam {
  group_id: string;
  group_name: string;
}

export interface RecipientJobTag {
  job_tag_id: string;
  tag_name: string;
}

export function labelForRecipient(r: Recipient): string {
  if (r._label) return r._label;
  if (r.recipient_type === 'email' && r.email) return r.email;
  return r.user_id ? 'User' : r.team_id ? 'Team' : 'Tag';
}

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type TabKey = 'user' | 'team' | 'job_tag' | 'email';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'user',    label: 'User',  icon: <PersonIcon sx={{ fontSize: 14 }} /> },
  { key: 'team',    label: 'Team',  icon: <TeamIcon   sx={{ fontSize: 14 }} /> },
  { key: 'job_tag', label: 'Tag',   icon: <TagIcon    sx={{ fontSize: 14 }} /> },
  { key: 'email',   label: 'Email', icon: <EmailIcon  sx={{ fontSize: 14 }} /> },
];

interface Props {
  recipients: Recipient[];
  onChange: (recs: Recipient[]) => void;
  users: RecipientUser[];
  teams: RecipientTeam[];
  jobTags: RecipientJobTag[];
  label?: string;
  emptyHint?: string;
}

const RecipientPicker: React.FC<Props> = ({
  recipients,
  onChange,
  users,
  teams,
  jobTags,
  label,
  emptyHint,
}) => {
  const [tab, setTab] = useState<TabKey>('user');
  const [freeEmail, setFreeEmail] = useState('');

  const addUser = (u: RecipientUser | null) => {
    if (!u) return;
    if (recipients.some(r => r.user_id === u.user_id)) return;
    onChange([...recipients, {
      recipient_type: 'user',
      user_id: u.user_id,
      _label: `${u.first_name} ${u.last_name}`.trim() || u.email,
    }]);
  };

  const addTeam = (t: RecipientTeam | null) => {
    if (!t) return;
    if (recipients.some(r => r.team_id === t.group_id)) return;
    onChange([...recipients, { recipient_type: 'team', team_id: t.group_id, _label: t.group_name }]);
  };

  const addTag = (j: RecipientJobTag | null) => {
    if (!j) return;
    if (recipients.some(r => r.job_tag_id === j.job_tag_id)) return;
    onChange([...recipients, { recipient_type: 'job_tag', job_tag_id: j.job_tag_id, _label: j.tag_name }]);
  };

  const addEmail = () => {
    const trimmed = freeEmail.trim().toLowerCase();
    if (!trimmed || !EMAIL_RX.test(trimmed)) return;
    if (recipients.some(r => r.recipient_type === 'email' && r.email?.toLowerCase() === trimmed)) return;
    onChange([...recipients, { recipient_type: 'email', email: trimmed, _label: trimmed }]);
    setFreeEmail('');
  };

  const remove = (idx: number) => onChange(recipients.filter((_, i) => i !== idx));

  return (
    <Box>
      {label && (
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {label}
        </Typography>
      )}

      {/* Tab selector */}
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {TABS.map(t => (
          <Chip
            key={t.key}
            icon={t.icon as React.ReactElement}
            label={t.label}
            size="small"
            variant={tab === t.key ? 'filled' : 'outlined'}
            color={tab === t.key ? 'primary' : 'default'}
            onClick={() => setTab(t.key)}
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Stack>

      {/* Input per tab */}
      {tab === 'user' && (
        <Autocomplete
          size="small"
          options={users}
          getOptionLabel={u => `${u.first_name} ${u.last_name}`.trim() || u.email}
          renderOption={(props, u) => (
            <li {...props} key={u.user_id}>
              <Box>
                <Typography variant="body2">{`${u.first_name} ${u.last_name}`.trim() || u.email}</Typography>
                <Typography variant="caption" color="text.secondary">{u.email}</Typography>
              </Box>
            </li>
          )}
          onChange={(_, v) => addUser(v)}
          renderInput={p => <TextField {...p} placeholder="Search users…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
        />
      )}
      {tab === 'team' && (
        <Autocomplete
          size="small"
          options={teams}
          getOptionLabel={t => t.group_name}
          onChange={(_, v) => addTeam(v)}
          renderInput={p => <TextField {...p} placeholder="Search teams…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.group_id === b.group_id}
        />
      )}
      {tab === 'job_tag' && (
        <Autocomplete
          size="small"
          options={jobTags}
          getOptionLabel={j => j.tag_name}
          onChange={(_, v) => addTag(v)}
          renderInput={p => <TextField {...p} placeholder="Search tags…" />}
          value={null}
          isOptionEqualToValue={(a, b) => a.job_tag_id === b.job_tag_id}
        />
      )}
      {tab === 'email' && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            type="email"
            label="External email"
            placeholder="name@company.com"
            value={freeEmail}
            onChange={e => setFreeEmail(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
          />
          <Button variant="outlined" size="small" onClick={addEmail} sx={{ mt: 0.5, flexShrink: 0 }}>Add</Button>
        </Stack>
      )}

      {/* Chips */}
      {recipients.length > 0 ? (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {recipients.map((r, i) => (
            <Chip
              key={i}
              label={labelForRecipient(r)}
              size="small"
              onDelete={() => remove(i)}
              color={
                r.recipient_type === 'user' ? 'primary'
                  : r.recipient_type === 'team' ? 'secondary'
                    : r.recipient_type === 'email' ? 'info'
                      : 'default'
              }
              variant="outlined"
            />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {emptyHint ?? 'No recipients added.'}
        </Typography>
      )}
    </Box>
  );
};

export default RecipientPicker;
